// NOTE: I didn't use useValidation here as I had to make custom validation for non native element (img)
import React, {
  useState,
  FormEvent,
  useEffect,
  memo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { validation } from "../../data";
import ConfirmModal from "./confirmModal";
import { ImgUpload } from ".";
import InvalidFd from "./invalidFd";
import SVGIcon from "./svgIcon";
import { StoreLawyer } from "../../redux/types";
import { loadCities } from "../../redux/actions/citiesActions";
import { loadCategories } from "../../redux/actions/categoriesActions";
import { RootState } from "../../redux/store";

// make all StoreLawyer properties optional
type Props = Partial<StoreLawyer> & {
  handleSubmit: (data: StoreLawyer) => void;
  handleCancel: () => void;
  isLoading: boolean;
};
const DashboardForm = memo((props: Props) => {
  const dispatch = useDispatch();
  const catEntity = useSelector(
    (state: RootState) => state.entities.categories
  );
  const cityEntity = useSelector((state: RootState) => state.entities.cities);

  useEffect(() => {
    dispatch(loadCities());
    dispatch(loadCategories());
  }, []); // eslint-disable-line

  type Errors = {
    [key: string]: ValidityState;
  };
  const [errors, setErrors] = useState<Errors>({});
  function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    const form = evt.currentTarget;
    // NOTE: image element is not native that's why I'm checking it manually
    if (form.checkValidity() && image && cover) {
      const data = { ...inputs };
      // add saudi country code to number if its not added by the user
      // by the pattern we applied to tel input, it will be just 9, 12 or 13 in length
      if (data.phone?.length === 9) {
        data.phone = "+966" + data.phone;
      } else if (data.phone?.length === 12) {
        data.phone = "+" + data.phone;
      }
      data.image = image; // add image to data
      data.cover = cover; // add cover to data
      props.handleSubmit(data as StoreLawyer);
      return;
    }

    // validation
    const invalidInputs: any = form.querySelectorAll(":invalid");
    const errors: Errors = {};
    invalidInputs.forEach((input: any) => {
      errors[input.name] = input.validity;
    });
    if (!image) errors.image = { valueMissing: true } as ValidityState;
    if (!cover) errors.cover = { valueMissing: true } as ValidityState;
    setErrors(errors);
    // focus first invalid input
    if (!image) imgRootRef.current?.focus();
    else if (!cover) coverRootRef.current?.focus();
    else {
      // (if doesn't have a name, that means its a fieldset)
      invalidInputs[0].name
        ? invalidInputs[0].focus()
        : invalidInputs[1].focus();
    }
  }

  // image file logic
  const imgRootRef = useRef<HTMLElement | null>(null);
  const [image, setImage] = useState(props.image);
  const handleImgChange = useCallback(
    (imageRootElm, path) => {
      imgRootRef.current = imageRootElm;
      // remove image error if any
      if (errors.image) delete errors.image;
      setImage(path);
    },
    [errors.image]
  );

  // cover file logic
  const coverRootRef = useRef<HTMLElement | null>(null);
  const [cover, setCover] = useState(props.cover);
  const handleCoverChange = useCallback(
    (coverRootRef, path) => {
      coverRootRef.current = coverRootRef;
      // remove image error if any
      if (errors.cover) delete errors.cover;
      setCover(path);
    },
    [errors.cover]
  );

  // inputs logic
  const [inputs, setInputs] = useState<Partial<StoreLawyer>>({
    username: props.username || "",
    name: props.name || "",
    category_id: props.category_id || ("" as any),
    legal_arguments: props.legal_arguments || "",
    legal_advice: props.legal_advice || "",
    documentation: props.documentation || "",
    city_id: props.city_id || ("" as any),
    phone: props.phone || "",
    email: props.email || "",
    facebook: props.facebook || "",
    instagram: props.instagram || "",
    twitter: props.twitter || "",
    linkedin: props.linkedin || "",
    snapchat: props.snapchat || "",
    whatsapp: props.whatsapp || "",
    contact_us_phrase: props.contact_us_phrase || "",
  });

  function handleInputChange(evt: any) {
    const curr = evt.currentTarget;
    setInputs({ ...inputs, [curr.name]: curr.value });

    // if form was validated, check if the input has been fixed
    // note that if all errors got fixed this will reset errors obj
    if (Object.keys(errors).length) {
      // remove it from errors obj if it was fixed
      if (curr.checkValidity()) {
        // notice that the line of code below does not trigger render as I mutated the state directly
        delete errors[curr.name];
      } else {
        // else update errors
        setErrors({ ...errors, [curr.name]: curr.validity });
      }
    }
  }

  // don't enable updating unless something has changed
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    // this means that we are in the add lawyer page so comparison here doesn't make sense
    if (props.email === undefined) return;

    if (image !== props.image) {
      if (disabled) setDisabled(false);
      return;
    }
    if (cover !== props.cover) {
      if (disabled) setDisabled(false);
      return;
    }

    for (const key of Object.keys(inputs)) {
      const inputVal = inputs[key as keyof StoreLawyer];
      const propVal = props[key as keyof Props];
      /* the second test is for nullable values
      as undefined (the prop) and empty string (the inputVal) cannot be equal,
      thus enabling the form when nothing has changed */
      if (
        String(inputVal).trim() !== String(propVal).trim() &&
        (inputVal || propVal)
      ) {
        if (disabled) setDisabled(false);
        return;
      }
    }
    // we reached here, this means nothing has changed
    if (!disabled) setDisabled(true);
  }, [inputs, image, cover, props]); // eslint-disable-line

  // show confirm modal on cancel if any input have been edited
  const [showCancelConf, setShowCancelConf] = useState(false);
  function handleCancel() {
    // edit lawyer form logic
    if (props.email) {
      if (!disabled) {
        setShowCancelConf(true);
      } else {
        props.handleCancel();
      }
      return;
    }

    // add lawyer form logic
    if (image) {
      setShowCancelConf(true);
      return;
    }
    for (const key of Object.keys(inputs)) {
      if (String(inputs[key as keyof StoreLawyer]).trim()) {
        setShowCancelConf(true);
        return;
      }
    }

    props.handleCancel();
  }

  return (
    <form
      className="container-fluid dashboard-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="row">
        <div className="col-lg-2">
          <fieldset className="mb-3">
            <legend className="sr-only">Picture</legend>
            <ImgUpload image={image} onChange={handleImgChange} />
            {errors.image && <InvalidFd message={validation.required} />}
            <p className="mt-3 mb-2">
              <label>صورة الغلاف:</label>
            </p>
            <ImgUpload image={cover} onChange={handleCoverChange} />
            {errors.cover && <InvalidFd message={validation.required} />}
          </fieldset>
        </div>
        <div className="col-lg-10">
          <fieldset className="dashboard-form__info">
            <legend className="sr-only">Information</legend>
            <div className="form-group">
              <label htmlFor="username">اسم المستخدم (الرابط)</label>
              <input
                type="text"
                name="username"
                id="username"
                className="form-control--bordered"
                placeholder="قم بانشاء اسم مستخدم"
                value={inputs.username}
                onChange={handleInputChange}
                required
              />
              {errors.username && <InvalidFd message={validation.required} />}
            </div>
            <div className="form-group">
              <label htmlFor="name">اسم المحامي</label>
              <input
                type="text"
                name="name"
                id="name"
                className="form-control--bordered"
                placeholder="اكتب اسم المحامي"
                value={inputs.name}
                onChange={handleInputChange}
                required
              />
              {errors.name && <InvalidFd message={validation.required} />}
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="category">التخصص</label>
                  <select
                    name="category_id"
                    id="category"
                    className="custom-select"
                    value={inputs.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      التخصص
                    </option>
                    {Object.keys(catEntity).map((catId) => {
                      return (
                        <option key={catId} value={catId}>
                          {catEntity[catId].name}
                        </option>
                      );
                    })}
                  </select>
                  {errors["category_id"] && (
                    <InvalidFd message={validation.required} />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="city">المدينة</label>
                  <select
                    name="city_id"
                    id="city"
                    className="custom-select"
                    value={inputs.city_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      المدينة
                    </option>
                    {Object.keys(cityEntity).map((cityId) => {
                      return (
                        <option key={cityId} value={cityId}>
                          {cityEntity[cityId].name}
                        </option>
                      );
                    })}
                  </select>
                  {errors["city_id"] && (
                    <InvalidFd message={validation.required} />
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="legal_arguments">المرافعات</label>
                  <textarea
                    name="legal_arguments"
                    id="legal_arguments"
                    className="form-control--bordered"
                    placeholder="اكتب الوصف"
                    rows={4}
                    value={inputs.legal_arguments}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  {errors["legal_arguments"] && (
                    <InvalidFd message={validation.required} />
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="legal_advice">الاستشارات القانونية</label>
                  <textarea
                    name="legal_advice"
                    id="legal_advice"
                    className="form-control--bordered"
                    placeholder="اكتب الوصف"
                    rows={4}
                    value={inputs.legal_advice}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  {errors["legal_advice"] && (
                    <InvalidFd message={validation.required} />
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="documentation">التوثيق</label>
                  <textarea
                    name="documentation"
                    id="documentation"
                    className="form-control--bordered"
                    placeholder="اكتب الوصف"
                    rows={4}
                    value={inputs.documentation}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  {errors.documentation && (
                    <InvalidFd message={validation.required} />
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="tel">الجوال</label>
                  <input
                    type="tel"
                    name="phone"
                    id="tel"
                    className="form-control--bordered"
                    placeholder="اكتب الجوال"
                    value={inputs.phone}
                    onChange={handleInputChange}
                    required
                    pattern="^(\+?966)?\d{9}"
                  />
                  {errors.phone && (
                    <InvalidFd
                      message={
                        errors.phone.valueMissing
                          ? validation.required
                          : validation.invalidPhone
                      }
                    />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="email">البريد الإلكتروني</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="form-control--bordered"
                    placeholder="اكتب البريد الإلكتروني"
                    value={inputs.email}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.email && (
                    <InvalidFd
                      message={
                        errors.email.valueMissing
                          ? validation.required
                          : validation.invalidEmail
                      }
                    />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="contact-us">جملة اتصل بنا</label>
                  <input
                    type="text"
                    name="contact_us_phrase"
                    id="contact-us"
                    className="form-control--bordered"
                    placeholder="الإفتراضية: يسعدنا تواصلك وتلقي استفساراتك، يرج..."
                    value={inputs.contact_us_phrase}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </fieldset>
          <fieldset className="social">
            <legend>مواقع التواصل</legend>
            <div className="row">
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#facebook" />
                  <input
                    type="url"
                    name="facebook"
                    aria-label="Facebook"
                    className="form-control--bordered"
                    placeholder="https://www.facebook.com/"
                    value={inputs.facebook}
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.facebook && (
                  <InvalidFd message={validation.invalidUrl} />
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#instagram" />
                  <input
                    type="url"
                    name="instagram"
                    aria-label="Instagram"
                    className="form-control--bordered"
                    placeholder="https://www.instegram.com/"
                    value={inputs.instagram}
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.instagram && (
                  <InvalidFd message={validation.invalidUrl} />
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#twitter" />
                  <input
                    type="url"
                    name="twitter"
                    aria-label="Twitter"
                    className="form-control--bordered"
                    placeholder="https://www.twitter.com/"
                    value={inputs.twitter}
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.twitter && (
                  <InvalidFd message={validation.invalidUrl} />
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#linkedin" />
                  <input
                    type="url"
                    name="linkedin"
                    aria-label="LinkedIn"
                    className="form-control--bordered"
                    placeholder="https://www.linkedin.com/"
                    value={inputs.linkedin}
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.linkedin && (
                  <InvalidFd message={validation.invalidUrl} />
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#snapchat" />
                  <input
                    type="url"
                    name="snapchat"
                    aria-label="Snapchat"
                    className="form-control--bordered"
                    placeholder="https://www.snapchat.com/"
                    value={inputs.snapchat}
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.snapchat && (
                  <InvalidFd message={validation.invalidUrl} />
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="form-group--icon">
                  <SVGIcon id="#whatsapp" />
                  <input
                    type="url"
                    name="whatsapp"
                    aria-label="WhatsApp"
                    className="form-control--bordered"
                    placeholder="https://api.whatsapp.com/"
                    value={inputs.whatsapp as string}
                    required
                    pattern="\S+" // spaces are not allowed in url
                    onChange={handleInputChange}
                  />
                </div>
                {errors.whatsapp && (
                  <InvalidFd
                    message={
                      errors.whatsapp.valueMissing
                        ? validation.required
                        : validation.invalidUrl
                    }
                  />
                )}
              </div>{" "}
            </div>
          </fieldset>
          <div className="mt-4">
            <button
              type="submit"
              className="btn btn-primary ml-4"
              disabled={disabled}
            >
              {props.name ? "حفظ" : "إضافة"}
              {props.isLoading && (
                <SVGIcon id="#spinner" className="loading-icon mr-1" />
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleCancel}
            >
              إلغاء
            </button>
          </div>
        </div>
      </div>
      <ConfirmModal
        show={showCancelConf}
        body="جميع التغييرات سيتم تجاهلها، هل انت متأكد؟"
        handleCancel={useCallback(() => setShowCancelConf(false), [])}
        handleConfirm={props.handleCancel}
      />
    </form>
  );
});
export default DashboardForm;
